// frontend/src/components/Modal.js


import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md',
  // Set showCloseButton to false by default if there's a footer
  showCloseButton,
  closeOnBackdropClick = true,
  footer
}) => {
  // Automatically hide close button if not explicitly set and has footer
  const shouldShowCloseButton = showCloseButton ?? !footer;
  const { background, text, border, cn } = useTheme();

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdropClick) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-6xl'
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(
    <div
      className={cn("fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto", background.overlay)}
      onClick={handleBackdropClick}
    >
      <div className={cn(`relative rounded-lg shadow-xl w-full mx-auto my-8 flex flex-col ${sizeClasses[size]} max-h-[calc(100vh-4rem)]`, background.elevated, border.default, "border")}>
        {/* Header - Fixed at top */}
        {(title || shouldShowCloseButton) && (
          <div className={cn("flex items-center justify-between p-4 flex-shrink-0", border.default, "border-b")}>
            {title && <h2 className={cn("text-xl font-semibold", text.primary)}>{title}</h2>}
            {shouldShowCloseButton && (
              <button
                onClick={onClose}
                className={cn("p-1 rounded-full transition-colors", "hover:bg-gray-100 dark:hover:bg-gray-700", text.secondary)}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Body - Scrollable */}
        <div className="p-4 overflow-y-auto flex-1">
          {children}
        </div>

        {/* Footer - Fixed at bottom */}
        {footer && (
          <div className={cn("border-t p-4 flex-shrink-0", border.default, background.secondary)}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// Example Usage
/* const ExampleDeleteConfirmation = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <button 
        onClick={() => setIsOpen(true)}
        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
      >
        Delete Item
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Confirm Delete"
        size="md"
        // No need to specify showCloseButton, it will hide automatically
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Handle delete
                setIsOpen(false);
              }}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Delete
            </button>
          </div>
        }
      >
        <p>Are you sure you want to delete this item? This action cannot be undone.</p>
      </Modal>
    </>
  );
}; */

export default Modal;